import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  miabApi,
  type MiabQuestion,
  type MiabSurvey,
  type MiabSurveyInput,
  type MiabQuestionInput,
  type MiabQType,
} from '../api/miab';
import { showToast } from './Toast';

/** `2026-07-20 00:00:00` / ISO → `2026-07-20T00:00` for datetime-local. */
function toLocalInput(v: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function toSqlDateTime(v: string): string {
  return v ? v.replace('T', ' ') + ':00' : '';
}
function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);
}

type OptDraft = { label: string; image_url: string; product_url: string; vendor: string };
type QDraft = {
  prompt: string;
  help_text: string;
  qtype: MiabQType;
  required: boolean;
  max_length: string;
  rating_min: string;
  rating_max: string;
  rating_min_label: string;
  rating_max_label: string;
  options: OptDraft[];
};

const emptyOpt = (): OptDraft => ({ label: '', image_url: '', product_url: '', vendor: '' });
const emptyQ = (): QDraft => ({
  prompt: '', help_text: '', qtype: 'multi', required: false,
  max_length: '', rating_min: '1', rating_max: '5',
  rating_min_label: '', rating_max_label: '', options: [emptyOpt()],
});

const QTYPE_LABEL: Record<MiabQType, string> = {
  single: 'Single choice',
  multi: 'Multi-select',
  rating: 'Rating (1–5)',
  text: 'Free text',
};

/**
 * Item photo for one rating option.
 *
 * Authoring an item used to mean committing the picture into the client bundle
 * and writing a migration to point the option at it. The file goes straight to
 * the server here instead, and what lands on the option is the URL it hands
 * back — so the stored shape is unchanged and pasted URLs (every option seeded
 * before this) keep working. The paste box stays available for the case that
 * still wants it: pointing at a vendor's own hosted image.
 */
function OptionImage({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (url: string) => void;
  disabled?: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [pasting, setPasting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  async function upload(file: File | undefined | null) {
    if (!file) return;
    setBusy(true);
    try {
      onChange(await miabApi.uploadImage(file));
    } catch (e) {
      showToast((e as Error).message, { variant: 'error' });
    } finally {
      setBusy(false);
      // Clear the input so re-picking the same file fires change again.
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div className="miab-opt-image">
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        hidden
        onChange={(e) => void upload(e.target.files?.[0])}
      />

      <div
        className={`miab-img-drop${dragOver ? ' is-over' : ''}${value ? ' has-img' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (!disabled && !busy) void upload(e.dataTransfer.files?.[0]);
        }}
        onClick={() => { if (!disabled && !busy) fileRef.current?.click(); }}
        title={value ? 'Click or drop a file to replace this image' : 'Click or drop a file to upload'}
      >
        {busy ? (
          <span className="miab-img-status">Uploading…</span>
        ) : value ? (
          <img src={value} alt="" loading="lazy" />
        ) : (
          <span className="miab-img-status">Drop image<br />or click</span>
        )}
      </div>

      <div className="miab-img-actions">
        <button type="button" disabled={busy || disabled}
          onClick={() => fileRef.current?.click()}>
          {value ? 'Replace' : 'Upload'}
        </button>
        {value && (
          <button type="button" className="btn-link-danger" disabled={busy || disabled}
            onClick={() => onChange('')}>Remove</button>
        )}
      </div>

      <button type="button" className="miab-img-alt" disabled={busy || disabled}
        onClick={() => setPasting((p) => !p)}>{pasting ? 'hide URL box' : 'or paste a URL'}</button>

      {pasting && (
        <input className="miab-img-url" placeholder="https://… or /static/miab/…"
          value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

type Props = {
  survey: MiabSurvey | null;
  questions: MiabQuestion[];
  locked: boolean;
  onDone: () => void;
  onCancel: () => void;
};

export default function MiabSurveyEditor({ survey, questions, locked, onDone, onCancel }: Props) {
  const queryClient = useQueryClient();
  const isNew = survey === null;

  const [form, setForm] = useState({
    slug: survey?.slug ?? '',
    title: survey?.title ?? '',
    box_label: survey?.box_label ?? '',
    intro: survey?.intro ?? '',
    opens_at: toLocalInput(survey?.opens_at ?? null),
    closes_at: toLocalInput(survey?.closes_at ?? null),
    order_window_start: survey?.order_window_start?.slice(0, 10) ?? '',
    order_window_end: survey?.order_window_end?.slice(0, 10) ?? '',
    is_published: Boolean(survey?.is_published),
  });

  const [qs, setQs] = useState<QDraft[]>([]);
  useEffect(() => {
    setQs(
      questions.length > 0
        ? questions.map((q) => ({
            prompt: q.prompt,
            help_text: q.help_text ?? '',
            qtype: q.qtype,
            required: q.required === 1,
            max_length: q.max_length != null ? String(q.max_length) : '',
            rating_min: q.rating_min != null ? String(q.rating_min) : '1',
            rating_max: q.rating_max != null ? String(q.rating_max) : '5',
            rating_min_label: q.rating_min_label ?? '',
            rating_max_label: q.rating_max_label ?? '',
            options: q.options.map((o) => ({
              label: o.label,
              image_url: o.image_url ?? '',
              product_url: o.product_url ?? '',
              vendor: o.vendor ?? '',
            })),
          }))
        : [emptyQ()],
    );
  }, [questions, survey?.id]);

  function setField<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  function setQ(i: number, patch: Partial<QDraft>) {
    setQs((prev) => prev.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  }
  function setOpt(qi: number, oi: number, patch: Partial<OptDraft>) {
    setQs((prev) =>
      prev.map((q, j) =>
        j === qi ? { ...q, options: q.options.map((o, k) => (k === oi ? { ...o, ...patch } : o)) } : q,
      ),
    );
  }
  function moveQ(i: number, dir: -1 | 1) {
    setQs((prev) => {
      const n = [...prev]; const j = i + dir;
      if (j < 0 || j >= n.length) return prev;
      [n[i], n[j]] = [n[j]!, n[i]!]; return n;
    });
  }

  const save = useMutation({
    mutationFn: async () => {
      const payload: MiabSurveyInput = {
        slug: form.slug || slugify(form.box_label || form.title),
        title: form.title,
        box_label: form.box_label,
        intro: form.intro || null,
        opens_at: toSqlDateTime(form.opens_at),
        closes_at: toSqlDateTime(form.closes_at),
        order_window_start: form.order_window_start || null,
        order_window_end: form.order_window_end || null,
        is_published: form.is_published,
      };
      const id = isNew
        ? (await miabApi.createSurvey(payload)).id
        : (await miabApi.updateSurvey(survey!.id, payload), survey!.id);

      if (!locked) {
        const questionsPayload: MiabQuestionInput[] = qs
          .filter((q) => q.prompt.trim())
          .map((q) => ({
            prompt: q.prompt.trim(),
            help_text: q.help_text.trim() || null,
            qtype: q.qtype,
            required: q.required,
            max_length: q.qtype === 'text' && q.max_length ? Number(q.max_length) : null,
            rating_min: q.qtype === 'rating' ? Number(q.rating_min) : null,
            rating_max: q.qtype === 'rating' ? Number(q.rating_max) : null,
            rating_min_label: q.qtype === 'rating' ? q.rating_min_label || null : null,
            rating_max_label: q.qtype === 'rating' ? q.rating_max_label || null : null,
            options:
              q.qtype === 'text'
                ? []
                : q.options
                    .filter((o) => o.label.trim())
                    .map((o) => ({
                      label: o.label.trim(),
                      image_url: o.image_url.trim() || null,
                      product_url: o.product_url.trim() || null,
                      vendor: o.vendor.trim() || null,
                    })),
          }));
        await miabApi.replaceQuestions(id, questionsPayload);
      }
      return id;
    },
    onSuccess: () => {
      showToast(isNew ? 'Survey created.' : 'Survey saved.');
      queryClient.invalidateQueries({ queryKey: ['miab'] });
      onDone();
    },
    onError: (e: Error) => showToast(e.message, { variant: 'error' }),
  });

  const datesOutOfOrder =
    form.opens_at && form.closes_at && new Date(form.opens_at) >= new Date(form.closes_at);
  const canSave =
    form.title.trim() && form.box_label.trim() && form.opens_at && form.closes_at && !datesOutOfOrder;

  return (
    <div className="miab-editor">
      <div className="miab-editor-head">
        <h2>{isNew ? 'New survey' : `Edit — ${survey!.box_label}`}</h2>
        <button className="btn" onClick={onCancel}>Cancel</button>
      </div>

      <div className="miab-form-grid">
        <label><span>Box</span>
          <input value={form.box_label} placeholder="November 2026"
            onChange={(e) => { setField('box_label', e.target.value); if (isNew && !form.slug) setField('slug', slugify(e.target.value)); }} />
        </label>
        <label><span>Title</span>
          <input value={form.title} placeholder="November Marketing in a Box — Item Survey"
            onChange={(e) => setField('title', e.target.value)} />
        </label>
        <label><span>Link slug</span>
          <input value={form.slug} placeholder="november-2026" onChange={(e) => setField('slug', slugify(e.target.value))} />
          <small>/miab/{form.slug || '…'}</small>
        </label>
        <label><span>Survey opens</span>
          <input type="datetime-local" value={form.opens_at} onChange={(e) => setField('opens_at', e.target.value)} />
        </label>
        <label><span>Survey closes</span>
          <input type="datetime-local" value={form.closes_at} onChange={(e) => setField('closes_at', e.target.value)} />
        </label>
        <label><span>Ordering opens</span>
          <input type="date" value={form.order_window_start} onChange={(e) => setField('order_window_start', e.target.value)} />
        </label>
        <label><span>Ordering closes</span>
          <input type="date" value={form.order_window_end} onChange={(e) => setField('order_window_end', e.target.value)} />
        </label>
        <label className="miab-form-wide"><span>Intro shown to franchisees</span>
          <textarea rows={2} value={form.intro} onChange={(e) => setField('intro', e.target.value)} placeholder="Help us choose what goes in the November box…" />
        </label>
      </div>

      {datesOutOfOrder && <div className="miab-notice">The close date is before the open date.</div>}

      <div className="miab-items-head">
        <h3>Questions {locked ? '' : `(${qs.filter((q) => q.prompt.trim()).length})`}</h3>
        {!locked && <button className="btn" onClick={() => setQs((p) => [...p, emptyQ()])}>+ Add question</button>}
      </div>

      {locked ? (
        <div className="miab-notice">
          This survey already has responses, so its questions are locked — changing
          them would invalidate answers already submitted. Dates and text can still
          be edited.
        </div>
      ) : (
        <div className="miab-qeditor-list">
          {qs.map((q, qi) => (
            <div className="miab-qeditor" key={qi}>
              <div className="miab-qeditor-top">
                <select value={q.qtype} onChange={(e) => setQ(qi, { qtype: e.target.value as MiabQType })}>
                  {(Object.keys(QTYPE_LABEL) as MiabQType[]).map((t) => (
                    <option key={t} value={t}>{QTYPE_LABEL[t]}</option>
                  ))}
                </select>
                <label className="miab-req-check">
                  <input type="checkbox" checked={q.required} onChange={(e) => setQ(qi, { required: e.target.checked })} /> Required
                </label>
                <div className="miab-item-actions">
                  <button className="btn-icon" title="Move up" onClick={() => moveQ(qi, -1)} disabled={qi === 0}>↑</button>
                  <button className="btn-icon" title="Move down" onClick={() => moveQ(qi, 1)} disabled={qi === qs.length - 1}>↓</button>
                  <button className="btn-icon btn-icon-danger" title="Remove" onClick={() => setQs((p) => p.filter((_, j) => j !== qi))}>×</button>
                </div>
              </div>

              <textarea className="miab-qeditor-prompt" rows={2} placeholder="Question prompt"
                value={q.prompt} onChange={(e) => setQ(qi, { prompt: e.target.value })} />
              <textarea className="miab-qeditor-help" rows={2} placeholder="Help text / background (optional)"
                value={q.help_text} onChange={(e) => setQ(qi, { help_text: e.target.value })} />

              {q.qtype === 'rating' && (
                <div className="miab-rating-config">
                  <label>Min <input type="number" value={q.rating_min} onChange={(e) => setQ(qi, { rating_min: e.target.value })} /></label>
                  <label>Max <input type="number" value={q.rating_max} onChange={(e) => setQ(qi, { rating_max: e.target.value })} /></label>
                  <label>Low label <input value={q.rating_min_label} placeholder="Not feeling it…" onChange={(e) => setQ(qi, { rating_min_label: e.target.value })} /></label>
                  <label>High label <input value={q.rating_max_label} placeholder="I love it!" onChange={(e) => setQ(qi, { rating_max_label: e.target.value })} /></label>
                </div>
              )}

              {q.qtype === 'text' && (
                <label className="miab-maxlen">Max characters
                  <input type="number" value={q.max_length} placeholder="e.g. 5000" onChange={(e) => setQ(qi, { max_length: e.target.value })} />
                </label>
              )}

              {q.qtype !== 'text' && (
                <div className="miab-opts">
                  <div className="miab-opts-label">
                    {q.qtype === 'rating' ? 'Items to rate' : 'Choices'}{q.qtype === 'rating' ? ' — drop an image on the thumbnail to upload it' : ''}
                  </div>
                  {q.options.map((o, oi) => (
                    <div className="miab-opt-row" key={oi}>
                      <input className="miab-opt-main" placeholder="Label" value={o.label} onChange={(e) => setOpt(qi, oi, { label: e.target.value })} />
                      {q.qtype === 'rating' && (
                        <>
                          <OptionImage value={o.image_url} onChange={(url) => setOpt(qi, oi, { image_url: url })} />
                          <input placeholder="Vendor URL" value={o.product_url} onChange={(e) => setOpt(qi, oi, { product_url: e.target.value })} />
                          <input placeholder="Vendor" value={o.vendor} onChange={(e) => setOpt(qi, oi, { vendor: e.target.value })} />
                        </>
                      )}
                      <button className="btn-icon btn-icon-danger" title="Remove" onClick={() =>
                        setQs((p) => p.map((qq, j) => j === qi ? { ...qq, options: qq.options.filter((_, k) => k !== oi) } : qq))
                      }>×</button>
                    </div>
                  ))}
                  <button className="btn miab-add-opt" onClick={() =>
                    setQs((p) => p.map((qq, j) => j === qi ? { ...qq, options: [...qq.options, emptyOpt()] } : qq))
                  }>+ Add {q.qtype === 'rating' ? 'item' : 'choice'}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="miab-editor-foot">
        <label className="miab-publish-toggle">
          <input type="checkbox" checked={form.is_published} onChange={(e) => setField('is_published', e.target.checked)} />
          <span>Published — franchisees see it once the open date passes. Leave off while building it.</span>
        </label>
        <button className="btn btn-primary" disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : isNew ? 'Create survey' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
